import { useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import _compact from 'lodash/compact'
import _find from 'lodash/find'
import _flatMap from 'lodash/flatMap'
import _get from 'lodash/get'
import _isEmpty from 'lodash/isEmpty'
import _isArray from 'lodash/isArray'
import _map from 'lodash/map'
import _parseInt from 'lodash/parseInt'
import _split from 'lodash/split'
import { COURSE_FILTER_FIELD } from './columns'

const COURSE_MODULES_QUERY = gql`
  query CourseModules($where: ContentWhereInput) {
    contentList(where: $where) {
      content {
        contentId
        contentType
        contentReferencesTo(types: ["course-module"]) {
          contentFrom {
            contentId
            contentType
          }
        }
      }
    }
  }
`

/**
 * Resolves the "Course" filter (multi-hop: course -> course modules -> videos) into
 * reference filter entries and loading state. When the user filters by course, we must
 * first fetch that course's module IDs, then add references for those modules (and the
 * course itself) so the content list query can return matching videos and course modules.
 *
 * @param {Object} filterModel - MUI DataGrid filter model (e.g. { items: [...] })
 * @returns {{ courseReferenceEntries: Array, courseModulesLoading: boolean, courseContentId: number|null }}
 */
export function useCourseFilter (filterModel) {
  const courseFilterItem = _find(
    filterModel?.items ?? [],
    (i) => i.field === COURSE_FILTER_FIELD && !_isEmpty(i.value),
  )
  const courseFilterValue = courseFilterItem?.value
  const courseValueStr = _isArray(courseFilterValue)
    ? _get(courseFilterValue, 0)
    : courseFilterValue
  const courseContentId = courseValueStr
    ? (() => {
        const parts = _split(courseValueStr, '-')
        return parts.length >= 2 ? _parseInt(parts[0]) : null
      })()
    : null

  const { data: courseModulesData, loading: courseModulesLoading } = useQuery(
    COURSE_MODULES_QUERY,
    {
      variables: {
        where: {
          contentId: { eq: courseContentId },
          contentType: { eq: 'course' },
        },
      },
      skip: !courseContentId,
      notifyOnNetworkStatusChange: true,
    },
  )

  const courseReferenceEntries = useMemo(() => {
    if (!courseContentId || !courseModulesData?.contentList?.content?.[0]) return []
    const courseContent = courseModulesData.contentList.content[0]
    const moduleRefs = _get(courseContent, 'contentReferencesTo', []) || []
    const modules = _compact(_map(moduleRefs, (r) => _get(r, ['contentFrom', 0])))
    const entries = _flatMap(modules, (m) => [
      { contentId: m.contentId, contentType: m.contentType, type: 'course-module-practice' },
      { contentId: m.contentId, contentType: m.contentType, type: 'course-module-other' },
    ])
    entries.push({
      contentId: courseContentId,
      contentType: 'course',
      type: 'course-module',
    })
    return entries
  }, [courseContentId, courseModulesData])

  return { courseReferenceEntries, courseModulesLoading, courseContentId }
}
